import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import React from 'react';

import { cn } from '../../utils/cn';
import { ButtonSpinner } from './spinner';

const buttonVariants = cva(
  'tw-inline-flex tw-items-center tw-justify-center tw-rounded-md tw-text-sm tw-font-medium tw-transition-colors focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-400 focus:tw-ring-offset-2 dark:hover:tw-bg-slate-800 dark:hover:tw-text-slate-100 disabled:tw-opacity-50 dark:focus:tw-ring-slate-400 disabled:tw-pointer-events-none dark:focus:tw-ring-offset-slate-900 data-[state=open]:tw-bg-slate-100 dark:data-[state=open]:tw-bg-slate-800 tw-border-none',
  {
    variants: {
      variant: {
        default:
          'tw-bg-[var(--color-primary)] tw-text-white hover:tw-bg-[var(--color-primary)] hover:tw-opacity-90 hover:tw-text-white',
        destructive: 'tw-bg-red-500 tw-text-white hover:tw-bg-red-600',
        outline:
          'tw-bg-transparent tw-border tw-border-solid tw-border-[#999] hover:tw-bg-gray-100',
        subtle: 'tw-bg-slate-100 tw-text-slate-900 hover:tw-bg-slate-200',
        ghost:
          'tw-bg-transparent hover:tw-bg-slate-100 data-[state=open]:tw-bg-transparent dark:data-[state=open]:tw-bg-transparent',
        link: 'tw-bg-transparent tw-underline-offset-4 hover:tw-underline tw-text-slate-900 hover:tw-bg-transparent',
        gray: 'tw-bg-gray-300 tw-text-gray-900 hover:tw-bg-gray-400',
      },
      size: {
        default: 'tw-h-10 tw-py-2 tw-px-4',
        sm: 'tw-h-9 tw-px-2 tw-rounded-md',
        lg: 'tw-h-11 tw-px-8 tw-rounded-md',
        xl: 'tw-h-14 tw-py-2 tw-px-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  loadingText?: string;
  uncolored?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loadingText, uncolored, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        type={props.type || 'button'}
        {...props}
      >
        {loadingText ? (
          <div className="tw-flex tw-flex-row tw-items-center">
            {ButtonSpinner}
            <span>{loadingText}</span>
          </div>
        ) : (
          props.children
        )}
      </button>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
